export const AFFILIATE_TERMS_VERSION = "2026-08-13";
export const AFFILIATE_CODE_PATTERN = /^ct-[a-z0-9]{8}$/;
export const AFFILIATE_VANITY_CODE_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])$/;

const AFFILIATE_RESERVED_CODES = new Set([
  "admin",
  "affiliate",
  "affiliates",
  "api",
  "billing",
  "chicky",
  "chicky-tutor",
  "chickytutor",
  "dashboard",
  "help",
  "login",
  "official",
  "payments",
  "privacy",
  "register",
  "signup",
  "support",
  "team",
  "terms",
  "www",
]);

export const AFFILIATE_ONBOARDING_STEPS = [
  { id: "accept_terms", label: "Accept the program terms" },
  { id: "copy_link", label: "Copy your tracked link" },
  { id: "learn_product", label: "Review the product overview" },
  { id: "review_claims", label: "Review approved and prohibited claims" },
  { id: "download_assets", label: "Download your first creative asset" },
  { id: "prepare_promotion", label: "Prepare a disclosed promotion" },
  { id: "review_payments", label: "Review payment setup" },
] as const;

export type AffiliateOnboardingStepId = (typeof AFFILIATE_ONBOARDING_STEPS)[number]["id"];

export const AFFILIATE_APPROVED_CLAIMS = [
  "ChickyTutor is an AI conversation partner for speaking practice.",
  "Learners can practise real-time voice conversations.",
  "ChickyTutor supports speaking practice in 70+ languages.",
  "Learners can receive prompts, hints, and corrections while they practise.",
  "ChickyTutor is designed to help learners build speaking confidence.",
] as const;

export const AFFILIATE_PROHIBITED_CLAIMS = [
  "Guaranteed fluency, exam results, employment, or a specific learning outcome.",
  "That ChickyTutor replaces every teacher, course, or form of language study.",
  "Features, prices, discounts, trials, or languages that are not currently available.",
  "Guaranteed affiliate income, conversion rates, or payout amounts.",
  "That an affiliate is ChickyTutor, an employee, or an official spokesperson.",
] as const;

export const AFFILIATE_AUDIENCES = [
  "Language learners who understand lessons but freeze when speaking.",
  "Travellers, immigrants, international students, and globally minded professionals.",
  "Learners who want low-pressure practice between classes or tutor sessions.",
  "Audiences interested in AI tools, education technology, travel, or international life.",
] as const;

export const AFFILIATE_DISCLOSURES = [
  "I may earn a commission if you subscribe through this link, at no extra cost to you.",
  "Affiliate link: I may receive a commission from ChickyTutor if you become a paying subscriber.",
  "This post contains a ChickyTutor affiliate link. I only recommend products I believe are useful.",
] as const;

export const AFFILIATE_ASSETS = [
  {
    id: "horizontal-lockup",
    type: "Logo · PNG",
    title: "Horizontal ChickyTutor lockup",
    description: "The primary partnership logo for light and warm-white backgrounds.",
    href: "/ui/brand-kit/lockups/chickytutor-lockup-horizontal-light-1080.png",
    previewHref: "/ui/brand-kit/lockups/chickytutor-lockup-horizontal-light-1080.png",
    previewKind: "image",
    previewAlt: "ChickyTutor horizontal logo",
  },
  {
    id: "face-mark",
    type: "Profile mark · PNG",
    title: "Chicky face mark",
    description: "A transparent square mark for profile images, badges, and compact placements.",
    href: "/ui/brand-kit/marks/chickytutor-face-mark-512.png",
    previewHref: "/ui/brand-kit/marks/chickytutor-face-mark-512.png",
    previewKind: "image",
    previewAlt: "ChickyTutor rooster face mark",
  },
  {
    id: "affiliate-campaign-artwork",
    type: "Campaign artwork · WEBP",
    title: "Affiliate campaign artwork",
    description: "Creator-ready artwork for articles, newsletters, and campaign landing content.",
    href: "/ui/brand-kit/web/chickytutor-affiliate-hero.webp",
    previewHref: "/ui/brand-kit/web/chickytutor-affiliate-hero.webp",
    previewKind: "image",
    previewAlt: "ChickyTutor affiliate campaign artwork",
  },
  {
    id: "social-card",
    type: "Social card · PNG",
    title: "Social sharing card",
    description: "A 1200 × 630 image for link previews, posts, newsletters, and video end frames.",
    href: "/ui/brand-kit/social/chickytutor-social-card-2026-08.png",
    previewHref: "/ui/brand-kit/social/chickytutor-social-card-2026-08.png",
    previewKind: "image",
    previewAlt: "ChickyTutor social sharing card",
  },
  {
    id: "product-demo",
    type: "Product video · MP4",
    title: "How ChickyTutor works",
    description:
      "A product-led explainer for reviews, reaction videos, articles, and social posts.",
    href: "/ui/chickytutor-how-it-works4.mp4",
    previewHref: "/ui/chickytutor-how-it-works4-thumbnail.jpg",
    previewKind: "video",
    previewAlt: "Preview of the ChickyTutor product explainer",
  },
] as const;

export const AFFILIATE_EXAMPLE_COPY = {
  social:
    "Knowing the words is different from using them in conversation. ChickyTutor gives you a low-pressure place to practise speaking with an AI conversation partner. Try it here: {{affiliate_link}}",
  emailSubject: "A useful way to practise speaking between lessons",
  emailBody:
    "If you understand your target language but hesitate when it is time to speak, ChickyTutor gives you a place to practise real voice conversations. It supports 70+ languages and is designed to help you build confidence through regular speaking practice. Try ChickyTutor: {{affiliate_link}}\n\nDisclosure: I may earn a commission if you subscribe through this link, at no extra cost to you.",
  talkingPoints: [
    "Lead with the learner problem: understanding more than they can comfortably say.",
    "Show or describe a real speaking-practice moment.",
    "Explain that ChickyTutor complements classes, tutors, and independent study.",
    "Tell people who it is best for and include a clear affiliate disclosure.",
  ],
} as const;

export function generateAffiliateCode(): string {
  return `ct-${crypto.randomUUID().replace(/-/g, "").slice(0, 8)}`;
}

export function normalizeAffiliateVanityCodeInput(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/^@+/, "")
    .replace(/[\s_.]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function validateAffiliateVanityCode(
  value: string
): { code: string; error: null } | { code: null; error: string } {
  const code = normalizeAffiliateVanityCodeInput(value);
  if (!AFFILIATE_VANITY_CODE_PATTERN.test(code)) {
    return {
      code: null,
      error: "Use 3–32 letters, numbers, or hyphens, beginning and ending with a letter or number.",
    };
  }
  if (code.startsWith("ct-") || AFFILIATE_RESERVED_CODES.has(code)) {
    return { code: null, error: "That code is reserved. Choose another." };
  }
  return { code, error: null };
}

export function normalizeAffiliateCode(value: string): string | null {
  const normalized = value.trim().toLowerCase();
  if (AFFILIATE_CODE_PATTERN.test(normalized)) return normalized;
  const vanityCode = validateAffiliateVanityCode(normalized);
  return vanityCode.error ? null : vanityCode.code;
}

export function buildAffiliateTrackedLink(code: string, origin = "https://www.chickytutor.com") {
  const normalized = normalizeAffiliateCode(code);
  if (!normalized) {
    throw new Error("Invalid affiliate code");
  }
  return `${origin.replace(/\/$/, "")}/r/${normalized}`;
}

export function mergeCompletedAffiliateSteps(
  completedSteps: string[],
  stepId: AffiliateOnboardingStepId
) {
  return Array.from(new Set([...completedSteps, stepId])).filter(
    (step): step is AffiliateOnboardingStepId =>
      AFFILIATE_ONBOARDING_STEPS.some((candidate) => candidate.id === step)
  );
}
